import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { fileUrl, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImagePlus, Loader2, Pencil, Plus, Trash2, ImageOff, LayoutGrid, Package } from "lucide-react";
import FilterChips from "@/components/FilterChips";

export default function AdminItems() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalFilter, setSucursalFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "piso", image_path: null, sucursal_id: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);


  const load = async () => {
    try {
      const [i, s] = await Promise.all([api.get("/items"), api.get("/sucursales")]);
      setItems(i.data);
      setSucursales(s.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      category: "piso",
      image_path: null,
      sucursal_id: me?.sucursal_id || sucursales[0]?.id || "",
    });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name,
      category: it.category || "piso",
      image_path: it.image_path,
      sucursal_id: it.sucursal_id || "",
    });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    if (!form.category) return toast.error("Elige una categoría");
    if (!form.sucursal_id) return toast.error("Selecciona una sucursal");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/items/${editing.id}`, form);
        setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Item actualizado");
      } else {
        const { data } = await api.post("/items", form);
        setItems((p) => [...p, data]);
        toast.success("Item creado");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}"?`)) return;
    try {
      await api.delete(`/items/${it.id}`);
      setItems((p) => p.filter((x) => x.id !== it.id));
      toast.success("Eliminado");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const filteredItems = useMemo(() => {
    let list = items;
    if (sucursalFilter !== "all") list = list.filter((it) => it.sucursal_id === sucursalFilter);
    if (categoryFilter !== "all") list = list.filter((it) => (it.category || "piso") === categoryFilter);
    return list;
  }, [items, sucursalFilter, categoryFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#4A3B32]" data-testid="items-title">Items</h1>
          <p className="text-[#8A796F]">Administra los productos del inventario.</p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold px-6 active:scale-95 transition-transform"
          data-testid="item-create-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo item
        </Button>
      </header>

      <div className="space-y-3">
        {sucursales.length > 1 && (
          <FilterChips
            value={sucursalFilter}
            onChange={setSucursalFilter}
            options={sucursales}
            allLabel="Todas las sucursales"
            testidPrefix="item-sucursal-filter"
          />
        )}
        <FilterChips
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[
            { id: "piso", name: "Piso" },
            { id: "produccion", name: "Producción" },
          ]}
          allLabel="Todas"
          testidPrefix="item-category-filter"
          icon={<Package className="w-4 h-4 text-[#8A796F]" />}
        />
      </div>

      {filteredItems.length === 0 ? (
        <div className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center" data-testid="items-empty">
          <p className="text-[#8A796F]">
            {items.length === 0 ? "Aún no hay items. Crea el primero." : "Sin items con estos filtros."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((it) => (
            <div key={it.id} className="bg-white border border-[#EADBCE] rounded-2xl p-4 flex gap-4" data-testid={`item-row-${it.id}`}>
              <div className="w-20 h-20 rounded-xl bg-[#FCF9F2] overflow-hidden shrink-0 flex items-center justify-center">
                {it.image_path ? (
                  <img src={fileUrl(it.image_path)} alt={it.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageOff className="w-6 h-6 text-[#EADBCE]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-[#4A3B32] truncate">{it.name}</h3>
                {it.sucursal && (
                  <p className="text-[10px] text-[#8A796F] mt-0.5">{it.sucursal.name}</p>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    it.category === "produccion"
                      ? "bg-[#C7E7F5]/60 text-[#2A6478]"
                      : "bg-[#F7C8E0]/40 text-[#4A3B32]"
                  }`}>
                    {it.category === "produccion" ? "Producción" : "Piso"}
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`item-edit-${it.id}`} className="rounded-full">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it)} data-testid={`item-delete-${it.id}`} className="rounded-full text-[#FF5C5C] hover:text-[#FF5C5C] hover:bg-[#FFF0F0]">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-[#EADBCE] sm:max-w-md" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar item" : "Nuevo item"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Imagen</Label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-24 rounded-xl bg-[#FCF9F2] border border-[#EADBCE] overflow-hidden flex items-center justify-center">
                  {form.image_path ? (
                    <img src={fileUrl(form.image_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImagePlus className="w-6 h-6 text-[#8A796F]" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileRef}
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="item-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-full border-[#EADBCE]"
                    data-testid="item-image-upload-button"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                    {form.image_path ? "Cambiar" : "Subir imagen"}
                  </Button>
                  <p className="text-xs text-[#8A796F] mt-1">JPG, PNG, WebP. Max 5MB.</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="item-name">Nombre</Label>
              <Input
                id="item-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Vasitos de helado"
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="item-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Sucursal</Label>
              <Select
                value={form.sucursal_id}
                onValueChange={(v) => setForm({ ...form, sucursal_id: v })}
              >
                <SelectTrigger className="h-12 rounded-xl border-[#EADBCE]" data-testid="item-sucursal-select">
                  <SelectValue placeholder="Selecciona una sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {sucursales.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8A796F]">Puedes crear items para cualquier sucursal.</p>
            </div>

            <div className="space-y-2">
              <Label>Categoría</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "piso", label: "Piso", desc: "Empleados en el mostrador" },
                  { value: "produccion", label: "Producción", desc: "Cocina / bodega" },
                ].map((c) => {
                  const active = form.category === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setForm({ ...form, category: c.value })}
                      data-testid={`item-category-${c.value}`}
                      className={`p-3 rounded-xl border-2 text-left transition-colors ${
                        active
                          ? "border-[#FF7878] bg-[#FFF0F0]"
                          : "border-[#EADBCE] bg-white hover:bg-[#FCF9F2]"
                      }`}
                    >
                      <div className="font-bold text-[#4A3B32] text-sm">{c.label}</div>
                      <div className="text-[10px] text-[#8A796F] mt-0.5">{c.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold"
              data-testid="item-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
